/** @jsxImportSource @emotion/react */
import { css } from '@emotion/react';

import { useCallback, useEffect, useState } from 'react';
import {
  App,
  Button as ButtonAnt,
  Col,
  Row,
  TableColumnsType,
  Tooltip,
  Typography
} from 'antd';
import { UserClientSearcher } from '@/components/forms/user-client-searcher.form';
import { Table } from '@/components/tables/table';
import { useAppDispatch, useAppSelector } from '@/redux/hooks';
import {
  getListUserClient,
  watchListUserClient,
  watchClientUserLoading,
  watchClientUserPagination,
  deleteUserClient,
  blockAndUnBlockUser
} from '@/redux/slices/client.slice';
import { PAGINATION_PARAMS } from '@/constants/pagination.constant';
import {
  DeleteOutlined,
  LockOutlined,
  UnlockOutlined
} from '@ant-design/icons';
import { formatDateToDDMMYYYY, tableScrollY } from '@/utils/common';
import usePermission from '@/hooks/permission.hook';

type PageChangeParams = {
  page?: number;
  pageSize?: number;
  filter?: FilterUserClient;
};

export default function UserClientManagement() {
  const { modal } = App.useApp();
  const dispatch = useAppDispatch();
  const listUserClient = useAppSelector(watchListUserClient);
  const pagination = useAppSelector(watchClientUserPagination);
  const loading = useAppSelector(watchClientUserLoading);

  const { updatable, deletable } = usePermission();

  const [filter, setFilter] = useState<FilterUserClient>({});

  const handleDeleteClientUser = async (userId: string) => {
    await dispatch(deleteUserClient(userId));
    const remainingRecords = listUserClient.length - 1;
    if (remainingRecords === 0 && pagination.curPage > 1) {
      handlePageChange({
        page: pagination.curPage - 1,
        pageSize: pagination.limitPage,
        filter
      });
    } else {
      handlePageChange({
        page: pagination.curPage,
        pageSize: pagination.limitPage,
        filter
      });
    }
  };

  const confirmDisableAccount = (userId: string) => {
    modal.confirm({
      title: 'Xác nhận Xóa tài khoản này?',
      content: (
        <em>
          Người dùng sẽ không thể truy cập, hành động này không thể hoàn tác và
          hành động vô hiệu hóa tài khoản người dùng của admin sẽ được ghi nhận
          tại mục “Ghi lại hoạt động của admin”
        </em>
      ),
      okText: 'Xác nhận',
      cancelText: 'Hủy',
      onOk: () => handleDeleteClientUser(userId)
    });
  };

  const handleBlockAndUnblock = async (type: string, id: string) => {
    await dispatch(
      blockAndUnBlockUser({
        type: type === 'active' ? 'block' : 'unblock',
        id: id
      })
    );
    await dispatch(
      getListUserClient({
        offset: PAGINATION_PARAMS.offset,
        limit: PAGINATION_PARAMS.limit
      })
    );
  };

  const confirmBlockUser = (type: string, id: string) => {
    modal.confirm({
      title: 'Xác nhận Khoá tài khoản người dùng?',
      content: (
        <em>
          Người dùng sẽ không thể truy cập, hành động này của admin sẽ được ghi
          nhận tại mục “Ghi lại hoạt động của admin”
        </em>
      ),
      okText: 'Xác nhận',
      cancelText: 'Hủy',
      onOk: () => handleBlockAndUnblock(type, id)
    });
  };

  const confirmUnblockUser = (type: string, id: string) => {
    modal.confirm({
      title: 'Xác nhận Mở khoá tài khoản người dùng?',
      content: (
        <em>
          Người dùng sẽ được phép truy cập hệ thống, hành động này của admin sẽ
          được ghi nhận tại mục “Ghi lại hoạt động của admin”
        </em>
      ),
      okText: 'Xác nhận',
      cancelText: 'Hủy',
      onOk: () => handleBlockAndUnblock(type, id)
    });
  };

  const handlePageChange = useCallback(
    ({
      page = PAGINATION_PARAMS.offset,
      pageSize = PAGINATION_PARAMS.limit,
      filter
    }: PageChangeParams) => {
      const age = filter?.age?.includes('all') ? undefined : filter?.age;
      dispatch(
        getListUserClient({
          offset: page,
          limit: pageSize,
          search: filter?.textSearch,
          gender: filter?.gender,
          province: filter?.province,
          status: filter?.status,
          age: JSON.stringify(age)
        })
      );
    },
    [dispatch]
  );

  const handleSearch = (values: FilterUserClient) => {
    const newFilter: FilterUserClient = {
      ...filter,
      ...values
    };
    setFilter(newFilter);
    handlePageChange({ filter: newFilter });
  };

  const handleReset = () => {
    handlePageChange({ filter });
  };

  useEffect(() => {
    handlePageChange({});
  }, [handlePageChange]);

  const columns: TableColumnsType<User> = [
    {
      title: 'User ID',
      width: 100,
      dataIndex: 'userIdMnmn',
      key: 'userIdMnmn',
      fixed: 'left'
    },
    {
      title: 'Tên hiển thị',
      width: 150,
      dataIndex: 'fullName',
      key: 'fullName',
      fixed: 'left'
    },
    {
      title: 'Số điện thoại',
      dataIndex: 'phone',
      key: 'phone',
      width: 150
    },
    {
      title: 'Email',
      dataIndex: 'email',
      key: 'email',
      width: 150
    },
    {
      title: 'Ngày sinh',
      dataIndex: 'bod',
      key: 'bod',
      width: 150,
      render: (record) => (record ? formatDateToDDMMYYYY(record) : '')
    },
    {
      title: 'Tuổi',
      dataIndex: 'age',
      key: 'age',
      width: 150
    },
    {
      title: 'Giới tính',
      dataIndex: 'gender',
      key: 'gender',
      width: 150
    },
    {
      title: 'Tỉnh thành',
      dataIndex: 'province',
      key: 'province',
      width: 150
    },
    {
      title: 'Trạng thái',
      dataIndex: 'statusActive',
      key: 'statusActive',
      width: 150,
      render: (record) => {
        return record === 'active' ? 'Đã kích hoạt' : 'Đã bị khoá';
      }
    },
    {
      title: 'Thao tác',
      key: 'action',
      fixed: 'right',
      width: 100,
      render: (record) => (
        <Row gutter={[8, 10]}>
          {updatable && (
            <Col>
              {record.statusActive === 'active' ? (
                <Tooltip title='khóa'>
                  <ButtonAnt
                    type='primary'
                    icon={<LockOutlined />}
                    onClick={() =>
                      confirmBlockUser(record.statusActive, record.userIdMnmn)
                    }
                  />
                </Tooltip>
              ) : (
                <Tooltip title='Mở khóa'>
                  <ButtonAnt
                    css={buttonUnlockStyles}
                    icon={<UnlockOutlined />}
                    onClick={() =>
                      confirmUnblockUser(record.statusActive, record.userIdMnmn)
                    }
                  />
                </Tooltip>
              )}
            </Col>
          )}
          {deletable && (
            <Col>
              <Tooltip title='Xoá'>
                <ButtonAnt
                  onClick={() => confirmDisableAccount(record.userIdMnmn)}
                  type='primary'
                  danger
                  icon={<DeleteOutlined />}
                />
              </Tooltip>
            </Col>
          )}
        </Row>
      )
    }
  ];

  return (
    <div css={rootStyles}>
      <Typography.Title level={5}>Quản lý người dùng</Typography.Title>
      <UserClientSearcher onSearch={handleSearch} />
      <Table<User>
        rowKey={(record) => record.userIdMnmn}
        columns={columns}
        dataSource={listUserClient}
        loading={loading}
        pagination={{
          showQuickJumper: true,
          showSizeChanger: true,
          current: pagination.curPage,
          pageSize: pagination.limitPage,
          total: pagination.totalRows,
          onChange: (page, pageSize) => {
            handlePageChange({ page, pageSize, filter });
          }
        }}
        scrollY={tableScrollY(9)}
        totalResult={pagination.totalRows}
        placeholder='Nhập từ khoá tìm kiếm Tên người dùng/ số điện thoại'
        onFilter={handleSearch}
        onReset={handleReset}
      />
    </div>
  );
}

const rootStyles = css`
  padding: 2rem 2.4rem;
`;

const buttonUnlockStyles = css`
  background-color: #48d218;
  border-color: transparent;
  .ant-btn-icon {
    color: #ffffff;
  }
  &:hover {
    background-color: #48d218 !important;
    border-color: transparent !important;
    .ant-btn-icon {
      color: #ffffff !important;
    }
  }
`;
